import { useForm } from "react-hook-form";
import DialogComponent from "../../../../../components/dialog-component/dialog-component";
import TextInputFormField from "../../../../../components/form-fields/text-input-field/text-input-form-field";
import DateFormField from "../../../../../components/form-fields/date-field/date-form-field";
import { AddNewPetModalProps, Breeds, Genders, Species } from "./types";
import {
  ModalBaseForm,
  ModalButtonsContainer,
} from "../../../../../components/dialog-component/dialog-component.styles";
import { PrimaryButton } from "../../../../../components/button/button";
import { useContext, useEffect, useState } from "react";
import {
  addPet,
  getAllBreeds,
  getAllGenders,
  getAllSpecies,
  getClients,
} from "../../../../../actions";
import SelectFormField from "../../../../../components/form-fields/select-field/select-form-field";
import { useAppDispatch } from "../../../../../store/hooks";
import { addNotification } from "../../../../../store/slices/ui-slice";
import { v4 as uuid } from "uuid";
import { AxiosError } from "axios";
import { petFormValidation } from "../../../../../utils/validations";
import { yupResolver } from "@hookform/resolvers/yup";
import { StepperDataContext } from "../../../context/stepper-context";
import { Client } from "../../initial-details/types";
import dayjs from "dayjs";

const ModalBody: React.FC<{
  breeds: Breeds;
  genders: Genders;
  species: Species;
  onClose: () => void;
  getSpecieBreeds: (species: number) => void;
}> = ({ breeds, genders, species, onClose, getSpecieBreeds }) => {
  const { selectedClient, setSelectedClient } = useContext(StepperDataContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const { control, watch, formState } = useForm<{
    name: string;
    breed: string;
    secondBreed?: string;
    species: string;
    birthdate: any;
    gender: string;
    color?: string;
  }>({
    mode: "all",
    resolver: yupResolver(petFormValidation()),
  });

  const updatePetsData = () => {
    getClients(`${selectedClient?.first_name} ${selectedClient?.last_name}`)
      .then((response) => {
        const { data } = response.data;
        const client = data.find(
          (client: Client) =>
            client.integration_id === selectedClient?.integration_id
        );
        setSelectedClient(client);
      })
      .catch((error: AxiosError) => {
        dispatch(
          addNotification({
            id: uuid(),
            type: "error",
            message: error.message,
          })
        );
      });
  };

  useEffect(() => {
    const { species } = watch();
    getSpecieBreeds(Number(species));
  }, [watch().species]);

  const handleSubmit = () => {
    const { name, breed, secondBreed, species, birthdate, gender, color } = watch();
    setIsLoading(true);
    const breeds = [
      { id: breed },
      ...(secondBreed ? [{ id: secondBreed }] : []),
    ];
    addPet({
      client_id: selectedClient?.integration_id,
      name: name,
      breeds: breeds,
      species: {
        id: species,
      },
      sex: {
        id: gender,
      },
      birthdate: dayjs(birthdate).format("YYYY-MM-DDTHH:mm:ssZ"),
      status: "active",
    })
      .then((response: any) => {
        const { message } = response.data;
        updatePetsData();
        dispatch(
          addNotification({
            id: uuid(),
            type: "success",
            message: message,
          })
        );
        onClose && onClose();
      })
      .catch((error: AxiosError) => {
        dispatch(
          addNotification({
            id: uuid(),
            type: "error",
            message: error.message,
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isValid = formState?.isValid;

  return (
    <ModalBaseForm>
      <TextInputFormField
        name="name"
        control={control}
        label="Name"
        placeholder="Enter here"
      />
      <SelectFormField
        name="species"
        control={control}
        label="Species"
        placeholder="Select species"
        options={species.map((x) => {
          return { label: x.label, value: x.integration_id };
        })}
      />
      <SelectFormField
        name="breed"
        control={control}
        label="Breed"
        placeholder="Select breed"
        options={breeds.map((x) => {
          return { label: x.label, value: x.integration_id };
        })}
      />
      <SelectFormField
        name="secondBreed"
        control={control}
        label="Second Breed"
        placeholder="Select second breed"
        options={breeds.map((x) => {
          return { label: x.label, value: x.integration_id };
        })}
      />
      <DateFormField
        name="birthdate"
        control={control}
        label="Date of Birth"
      />
      <SelectFormField
        name="gender"
        control={control}
        label="Gender"
        placeholder="Select gender"
        options={genders.map((x) => {
          return { label: x.label, value: x.integration_id };
        })}
      />
      <TextInputFormField
        name="color"
        control={control}
        label="Color"
        placeholder="Patient color"
      />
      <ModalButtonsContainer>
        <PrimaryButton
          isLoading={isLoading}
          disabled={!isValid}
          onClick={handleSubmit}
        >
          Add pet
        </PrimaryButton>
      </ModalButtonsContainer>
    </ModalBaseForm>
  );
};

const AddNewPetModal: React.FC<AddNewPetModalProps> = ({ open, onClose }) => {
  const [breeds, setBreeds] = useState<Breeds>([]);
  const [genders, setGenders] = useState<Genders>([]);
  const [species, setSpecies] = useState<Species>([]);

  const dispatch = useAppDispatch();

  const getSpecieBreeds = (specie: number) => {
    if (specie) {
      getAllBreeds(specie)
        .then((response) => {
          const { data } = response.data;
          setBreeds(data);
        })
        .catch((error: AxiosError) => {
          dispatch(
            addNotification({
              id: uuid(),
              type: "error",
              message: error.message,
            })
          );
        });
    } else {
      setBreeds([]);
    }
  };

  useEffect(() => {
    if (open) {
      getAllGenders()
        .then((response) => {
          const { data } = response.data;
          setGenders(data);
        })
        .catch((error: AxiosError) => {
          dispatch(
            addNotification({
              id: uuid(),
              type: "error",
              message: error.message,
            })
          );
        });
      getAllSpecies()
        .then((response) => {
          const { data } = response.data;
          setSpecies(data);
        })
        .catch((error: AxiosError) => {
          dispatch(
            addNotification({
              id: uuid(),
              type: "error",
              message: error.message,
            })
          );
        });
    }
  }, [open]);

  return (
    <>
      <DialogComponent
        title="Add new pet"
        content={
          <ModalBody
            breeds={breeds}
            genders={genders}
            species={species}
            onClose={onClose}
            getSpecieBreeds={getSpecieBreeds}
          />
        }
        open={open}
        onClose={onClose}
      />
    </>
  );
};

export default AddNewPetModal;
